// src/components/purchases/RecordPurchaseModal.tsx
// Modal for recording new purchase transactions with line items
// Updated: Product Catalog integration with ProductSelector

import React, { useState, useEffect } from 'react';
import { X, Calendar, Plus, Trash2, ArrowLeft, BookOpen } from 'lucide-react';
import { purchasesAPI } from '../../services/purchasesAPI';
import { purchaseLineItemsAPI } from '../../services/purchaseLineItemsAPI';
import ProductSelector from '../product/ProductSelector';
import AddProductModal from '../product/AddProductModal';
import { productCatalogAPI, Product } from '../../services/productCatalogAPI';
import type { CategoryHierarchy } from '../../types/category';
import type { CreateLineItemRequest, PurchaseLineItem } from '../../types/purchase';
import { logger } from '../../utils/logger';

const modalLogger = logger.createServiceLogger('RecordPurchaseModal');

interface RecordPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  categories: CategoryHierarchy[];
  initialProducts?: Product[];
}

type Step = 'header' | 'line-items';

const RecordPurchaseModal: React.FC<RecordPurchaseModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave,
  categories,
  initialProducts
}) => {
  const [step, setStep] = useState<Step>('header');
  const [purchaseId, setPurchaseId] = useState<string>('');
  
  // Header form data
  const [headerData, setHeaderData] = useState({
    purchaseDate: new Date().toISOString().split('T')[0],
    vendor: '',
    poNumber: '',
    invoiceNumber: '',
    notes: ''
  });

  // Product selection state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);

  // Line item form data
  const [lineItemData, setLineItemData] = useState({
    itemDescription: '',
    categoryId: '',
    subcategoryId: '',
    quantity: 1,
    unitCost: 0,
    productId: '' // NEW: Track productId for catalog items
  });

  const [lineItems, setLineItems] = useState<PurchaseLineItem[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
 const [loading, setLoading] = useState(false);
 const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [pendingLineItemData, setPendingLineItemData] = useState<typeof lineItemData | null>(null);
  const [pendingInitialProducts, setPendingInitialProducts] = useState<Product[]>([]);
  const [pendingProductQuantities, setPendingProductQuantities] = useState<Record<string, { quantity: number; unitCost: number }>>({});

  // Calculate totals
  const totalCost = lineItems.reduce((sum, item) => sum + item.totalCost, 0);
  const lineItemTotalCost = lineItemData.quantity * lineItemData.unitCost;

  useEffect(() => {
    if (!isOpen) {
      resetModal();
    }
  }, [isOpen]);

  // Handle initial products from Product Catalog
  useEffect(() => {
    if (isOpen && initialProducts && initialProducts.length > 0) {
      modalLogger.info('Modal opened with initial products', { count: initialProducts.length });
      setPendingInitialProducts(initialProducts);
      // Initialize quantities as empty (user must enter) with unit costs from catalog
      const initialQuantities: Record<string, { quantity: number; unitCost: number }> = {};
      initialProducts.forEach(p => {
        initialQuantities[p.productId] = { 
          quantity: 0, // Blank - user must enter
          unitCost: p.typicalUnitCost || 0 
        };
      });
      setPendingProductQuantities(initialQuantities);
    }
  }, [isOpen, initialProducts]);

 // Note: pendingInitialProducts are now added manually via "Add All to Purchase" button

  const handlePendingQuantityChange = (productId: string, field: 'quantity' | 'unitCost', value: number) => {
    setPendingProductQuantities(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }));
  };

  const handleRemovePendingProduct = (productId: string) => {
    setPendingInitialProducts(prev => prev.filter(p => p.productId !== productId));
    setPendingProductQuantities(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const validatePendingProducts = (): boolean => {
    for (const product of pendingInitialProducts) {
      const qty = pendingProductQuantities[product.productId]?.quantity || 0;
      if (qty <= 0) {
        setErrors({ pendingProducts: 'All products must have a quantity greater than 0' });
        return false;
      }
    }
    return true;
  };

  const handleAddAllPendingProducts = async () => {
    if (!validatePendingProducts()) return;
    
    setLoading(true);
    modalLogger.info('Adding all pending products as line items', { count: pendingInitialProducts.length });
    
    try {
      for (const product of pendingInitialProducts) {
        const quantityData = pendingProductQuantities[product.productId];
        
        // Find parent category for the product's subcategory
        let parentCategoryId = '';
        if (product.subcategoryId) {
          const subcat = categories.find(c => c.categoryId === product.subcategoryId);
          if (subcat?.parentCategoryId) {
            parentCategoryId = subcat.parentCategoryId;
          }
        } else if (product.categoryId) {
          const cat = categories.find(c => c.categoryId === product.categoryId);
          if (cat && !cat.parentCategoryId) {
            parentCategoryId = product.categoryId;
          } else if (cat?.parentCategoryId) {
            parentCategoryId = cat.parentCategoryId;
          }
        }

        // Build description
        let description = product.name;
        if (product.manufacturer || product.model) {
          const details = [product.manufacturer, product.model].filter(Boolean).join(' ');
          description = `${product.name} - ${details}`;
        }

        const lineItemRequest: CreateLineItemRequest = {
          itemDescription: description,
          categoryId: parentCategoryId || product.categoryId,
          subcategoryId: product.subcategoryId || product.categoryId,
          quantity: quantityData.quantity,
          unitCost: quantityData.unitCost,
          productId: product.productId
        };

        const createdLineItem = await purchaseLineItemsAPI.createLineItem(purchaseId, lineItemRequest);

        const parentCat = categories.find(c => c.categoryId === lineItemRequest.categoryId);
        const subCat = categories.find(c => c.categoryId === lineItemRequest.subcategoryId);

        const enrichedLineItem = {
          ...createdLineItem,
          categoryName: parentCat?.name,
          subcategoryName: subCat?.name,
          productId: product.productId
        };

        setLineItems(prev => [...prev, enrichedLineItem]);
        modalLogger.debug('Added pending product as line item', { productId: product.productId });
      }
      
      // Clear pending products after successful addition
      setPendingInitialProducts([]);
      setPendingProductQuantities({});
      setErrors({});
    } catch (error) {
      modalLogger.error('Failed to add pending products', undefined, error as Error);
      setErrors({ pendingProducts: `Failed to add products: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  };
  const resetModal = () => {
    setStep('header');
    setPurchaseId('');
    setHeaderData({
      purchaseDate: new Date().toISOString().split('T')[0],
      vendor: '',
      poNumber: '',
      invoiceNumber: '',
      notes: ''
    });
    resetLineItemForm();
    setLineItems([]);
    setErrors({});
    setPendingInitialProducts([]);
    setPendingProductQuantities({});
  };

  const resetLineItemForm = () => {
    setSelectedProduct(null);
    setIsManualEntry(false);
    setLineItemData({
      itemDescription: '',
      categoryId: '',
      subcategoryId: '',
      quantity: 1,
      unitCost: 0,
      productId: ''
    });
  };

  // Handle product selection from catalog
  const handleProductSelected = (product: Product | null) => {
    setSelectedProduct(product);
    setIsManualEntry(false);
    
    if (product) {
      modalLogger.debug('Product selected, auto-populating fields', { 
        productId: product.productId, 
        name: product.name 
      });
      
      // Find parent category for the product's subcategory
      let parentCategoryId = '';
      if (product.subcategoryId) {
        const subcat = categories.find(c => c.categoryId === product.subcategoryId);
        if (subcat?.parentCategoryId) {
          parentCategoryId = subcat.parentCategoryId;
        }
      } else if (product.categoryId) {
        // Product might have categoryId as parent
        const cat = categories.find(c => c.categoryId === product.categoryId);
        if (cat && !cat.parentCategoryId) {
          parentCategoryId = product.categoryId;
        } else if (cat?.parentCategoryId) {
          parentCategoryId = cat.parentCategoryId;
        }
      }

      // Build description from product details
      let description = product.name;
      if (product.manufacturer || product.model) {
        const details = [product.manufacturer, product.model].filter(Boolean).join(' ');
        description = `${product.name} - ${details}`;
      }

      setLineItemData({
        itemDescription: description,
        categoryId: parentCategoryId || product.categoryId,
        subcategoryId: product.subcategoryId || product.categoryId,
        quantity: 1,
        unitCost: product.typicalUnitCost || 0,
        productId: product.productId
      });
    }
  };

 // Handle manual entry mode - now opens AddProductModal
  const handleManualEntry = () => {
    modalLogger.debug('Opening Add Product modal');
    setShowAddProductModal(true);
  };

  // Switch back to catalog selection
  const handleSwitchToCatalog = () => {
    modalLogger.debug('Switching back to catalog selection');
    setIsManualEntry(false);
    resetLineItemForm();
  };

  const validateHeader = () => {
    const newErrors: Record<string, string> = {};
    if (!headerData.purchaseDate) newErrors.purchaseDate = 'Purchase date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateLineItem = () => {
    const newErrors: Record<string, string> = {};
    if (!lineItemData.itemDescription.trim()) newErrors.itemDescription = 'Item description is required';
    if (!lineItemData.productId) newErrors.productId = 'Please select a product from the catalog';
    if (lineItemData.quantity <= 0) newErrors.quantity = 'Quantity must be greater than 0';
    if (lineItemData.unitCost < 0) newErrors.unitCost = 'Unit cost cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToLineItems = async () => {
    if (!validateHeader()) return;
    
    // If we have pending initial products, create the purchase header now
    if (pendingInitialProducts.length > 0 && !purchaseId) {
      setLoading(true);
      try {
        // Create purchase header with first product's data for the temp record
        const firstProduct = pendingInitialProducts[0];
        const tempItemId = `TEMP_${Date.now()}`;
        const purchaseData = {
          itemId: tempItemId,
          purchaseDate: headerData.purchaseDate,
          itemName: firstProduct.name,
          quantity: 1,
          unitCost: firstProduct.typicalUnitCost || 0,
          categoryId: firstProduct.subcategoryId || firstProduct.categoryId,
          vendor: headerData.vendor || undefined,
          poNumber: headerData.poNumber || undefined,
          invoiceNumber: headerData.invoiceNumber || undefined,
          notes: headerData.notes || undefined
        };

        const response = await purchasesAPI.createPurchase(purchaseData);
        
        if (response.success && response.data) {
          setPurchaseId(response.data.purchaseId);
          modalLogger.info('Purchase header created for initial products', { 
            purchaseId: response.data.purchaseId 
          });
        } else {
          throw new Error(response.error?.message || 'Failed to create purchase');
        }
      } catch (error) {
        modalLogger.error('Failed to create purchase header', undefined, error as Error);
        setErrors({ header: `Failed to create purchase: ${(error as Error).message}` });
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    
    setStep('line-items');
    modalLogger.info('Moving to line items step', { headerData });
  };

  const createPurchaseHeader = async (): Promise<string> => {
    const tempItemId = `TEMP_${Date.now()}`;
    const purchaseData = {
      itemId: tempItemId,
      purchaseDate: headerData.purchaseDate,
      itemName: lineItemData.itemDescription,
      quantity: lineItemData.quantity,
      unitCost: lineItemData.unitCost,
      categoryId: lineItemData.subcategoryId,
      vendor: headerData.vendor || undefined,
      poNumber: headerData.poNumber || undefined,
      invoiceNumber: headerData.invoiceNumber || undefined,
      notes: headerData.notes || undefined
    };

    const response = await purchasesAPI.createPurchase(purchaseData);
    
    if (response.success && response.data) {
      modalLogger.info('Purchase header created', { purchaseId: response.data.purchaseId });
      return response.data.purchaseId;
    } else {
      throw new Error(response.error?.message || 'Failed to create purchase');
    }
  };

 const handleAddLineItem = async (productIdOverride?: string) => {
    if (!validateLineItem()) return;

    // Product catalog is source of truth - all items must have productId
    if (!lineItemData.productId && !productIdOverride) {
      setErrors({ lineItem: 'Please select a product from the catalog or create a new one' });
      return;
    }

    setLoading(true);
    try {
      let currentPurchaseId = purchaseId;
      if (!purchaseId) {
        currentPurchaseId = await createPurchaseHeader();
        setPurchaseId(currentPurchaseId);
      }

      const lineItemRequest: CreateLineItemRequest = {
        itemDescription: lineItemData.itemDescription,
        categoryId: lineItemData.categoryId,
        subcategoryId: lineItemData.subcategoryId,
        quantity: lineItemData.quantity,
        unitCost: lineItemData.unitCost,
        productId: (productIdOverride && productIdOverride !== 'skip') ? productIdOverride : (lineItemData.productId || undefined)
      };

      const createdLineItem = await purchaseLineItemsAPI.createLineItem(
        currentPurchaseId,
        lineItemRequest
      );

      const parentCat = categories.find(c => c.categoryId === lineItemData.categoryId);
      const subCat = categories.find(c => c.categoryId === lineItemData.subcategoryId);
      
      const enrichedLineItem = {
        ...createdLineItem,
        categoryName: parentCat?.name,
        subcategoryName: subCat?.name,
        productId: lineItemData.productId || undefined
      };

      setLineItems([...lineItems, enrichedLineItem]);
      resetLineItemForm();
      setErrors({});
      
      modalLogger.info('Line item added', { 
        lineItemId: createdLineItem.lineItemId,
        fromCatalog: Boolean(lineItemData.productId)
      });
    } catch (error) {
      modalLogger.error('Failed to add line item', undefined, error as Error);
      setErrors({ lineItem: `Failed to add line item: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLineItem = async (lineItemId: string) => {
    try {
      await purchaseLineItemsAPI.deleteLineItem(purchaseId, lineItemId);
      setLineItems(lineItems.filter(item => item.lineItemId !== lineItemId));
      modalLogger.info('Line item removed', { lineItemId });
    } catch (error) {
      modalLogger.error('Failed to remove line item', undefined, error as Error);
      window.alert('Failed to remove line item. Please try again.');
    }
  };

  const handleFinish = () => {
    if (lineItems.length === 0) {
      setErrors({ lineItems: 'Please add at least one line item before finishing.' });
      return;
    }

    modalLogger.info('Purchase completed', { purchaseId, lineItemCount: lineItems.length });
    onSave();
    onClose();
    resetModal();
  };

 const handleCancel = () => {
    if (step === 'line-items' && lineItems.length > 0) {
      if (!window.confirm('You have unsaved line items. Are you sure you want to cancel?')) {
        return;
      }
    }
    onClose();
    resetModal();
  };

  // Handle product created from AddProductModal
  const handleProductCreated = (newProduct: Product) => {
    modalLogger.info('New product created, auto-selecting', { 
      productId: newProduct.productId, 
      name: newProduct.name 
    });
    
    setShowAddProductModal(false);
    
    // Auto-select the newly created product
    handleProductSelected(newProduct);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200" style={{ backgroundColor: '#f8fafc' }}>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Record Purchase</h2>
            <p className="text-sm text-gray-600 mt-1">
              {step === 'header' ? 'Step 1: Enter purchase information' : `Step 2: Add line items (${lineItems.length} added)`}
            </p>
          </div>
          <button onClick={handleCancel} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Step 1: Header Form */}
        {step === 'header' && (
          <div className="p-6 space-y-6">
            {errors.header && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {errors.header}
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Purchase Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purchase Date <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="date" 
                    value={headerData.purchaseDate} 
                    onChange={(e) => setHeaderData({ ...headerData, purchaseDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.purchaseDate ? 'border-red-500' : 'border-gray-300'
                    }`} 
                  />
                  {errors.purchaseDate && <p className="text-red-500 text-xs mt-1">{errors.purchaseDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                  <input 
                    type="text" 
                    value={headerData.vendor} 
                    onChange={(e) => setHeaderData({ ...headerData, vendor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
                    placeholder="e.g., GALLS, Keith's Sporting Goods" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                  <input 
                    type="text" 
                    value={headerData.poNumber} 
                    onChange={(e) => setHeaderData({ ...headerData, poNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
                    placeholder="e.g., PO-2024-001" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input 
                    type="text" 
                    value={headerData.invoiceNumber} 
                    onChange={(e) => setHeaderData({ ...headerData, invoiceNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
                    placeholder="e.g., INV-5678" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea 
                  value={headerData.notes} 
                  onChange={(e) => setHeaderData({ ...headerData, notes: e.target.value })}
                  rows={3} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" 
                  placeholder="Optional notes about this purchase..." 
                />
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={handleCancel} 
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors mr-3"
              >
                Cancel
              </button>
              <button 
                type="button"
                onClick={handleContinueToLineItems}
                className="px-4 py-2 text-white rounded-lg transition-colors" 
                style={{ backgroundColor: '#082759' }} 
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0a2e6b')} 
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#082759')}
              >
                Continue to Line Items →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Line Items Form */}
        {step === 'line-items' && (
          <div className="p-6 space-y-6">
            {errors.lineItem && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {errors.lineItem}
              </div>
            )}

          {errors.lineItems && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {errors.lineItems}
              </div>
            )}

            {errors.pendingProducts && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {errors.pendingProducts}
              </div>
            )}

            {/* Products from Catalog - Only show if there are pending products */}
            {pendingInitialProducts.length > 0 && (
              <div className="space-y-4 bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-green-600" />
                  Products to Order ({pendingInitialProducts.length})
                </h3>
                <p className="text-sm text-gray-600">
                  Enter quantities for each product, then click "Add All to Purchase"
                </p>
                
                <div className="space-y-3">
                  {pendingInitialProducts.map(product => {
                    const quantityData = pendingProductQuantities[product.productId] || { quantity: 0, unitCost: 0 };
                    const lineTotal = quantityData.quantity * quantityData.unitCost;
                    
                    return (
                      <div key={product.productId} className="bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            {product.manufacturer && (
                              <p className="text-sm text-gray-500">{product.manufacturer} {product.model || ''}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemovePendingProduct(product.productId)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Remove from order"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Quantity <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={quantityData.quantity || ''}
                              onChange={(e) => handlePendingQuantityChange(
                                product.productId, 
                                'quantity', 
                                parseInt(e.target.value) || 0
                              )}
                              placeholder="Enter qty"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Cost ($)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={quantityData.unitCost}
                              onChange={(e) => handlePendingQuantityChange(
                                product.productId, 
                                'unitCost', 
                                parseFloat(e.target.value) || 0
                              )}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Total</label>
                            <div className="px-2 py-1.5 text-sm bg-gray-50 border border-gray-300 rounded text-gray-700 font-medium">
                              ${lineTotal.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={handleAddAllPendingProducts}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 font-medium"
                >
                  {loading ? 'Adding...' : `Add All ${pendingInitialProducts.length} Products to Purchase`}
                </button>
              </div>
            )}

            {/* Add Line Item Form */}
            <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Plus className="w-5 h-5 mr-2 text-blue-600" />
                Add Line Item
              </h3>

              {/* Product Selector - Only show if not in manual entry mode */}
              {!isManualEntry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <BookOpen className="w-4 h-4 mr-1 text-blue-600" />
                    Select from Product Catalog
                  </label>
                  <ProductSelector
                    onProductSelected={handleProductSelected}
                    onManualEntry={handleManualEntry}
                    selectedProduct={selectedProduct}
                    categories={categories}
                  />
                </div>
              )}
             
              {/* Item Details Form - Always visible */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Item Description <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  value={lineItemData.itemDescription} 
                  onChange={(e) => setLineItemData({ ...lineItemData, itemDescription: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.itemDescription ? 'border-red-500' : 'border-gray-300'
                  }`} 
                  placeholder="e.g., 5.11 Tactical Shirt - Size L" 
                />
                {errors.itemDescription && <p className="text-red-500 text-xs mt-1">{errors.itemDescription}</p>}
              </div>
             
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number" 
                    min="1"
                    value={lineItemData.quantity} 
                    onChange={(e) => setLineItemData({ ...lineItemData, quantity: parseInt(e.target.value) || 0 })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.quantity ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Cost ($) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number" 
                    min="0"
                    step="0.01"
                    value={lineItemData.unitCost} 
                    onChange={(e) => setLineItemData({ ...lineItemData, unitCost: parseFloat(e.target.value) || 0 })}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.unitCost ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.unitCost && <p className="text-red-500 text-xs mt-1">{errors.unitCost}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-medium">
                    ${lineItemTotalCost.toFixed(2)}
                  </div>
                </div>
              </div>

              <button 
                type="button"
                onClick={() => handleAddLineItem()}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Adding...' : '+ Add Line Item'}
              </button>
            </div>

            {/* Line Items List */}
            {lineItems.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Line Items ({lineItems.length}) - Total: ${totalCost.toFixed(2)}
                </h3>
                <div className="space-y-2">
                  {lineItems.map((item, index) => (
                    <div key={item.lineItemId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-500">#{index + 1}</span>
                          <p className="text-sm font-medium text-gray-900">{item.itemDescription}</p>
                          {(item as any).productId && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              Catalog
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {item.categoryName} › {item.subcategoryName} • Qty: {item.quantity} @ ${item.unitCost.toFixed(2)} = ${item.totalCost.toFixed(2)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveLineItem(item.lineItemId)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove line item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={() => setStep('header')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Header</span>
              </button>
              <button 
                type="button"
                onClick={handleFinish}
                disabled={lineItems.length === 0}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:bg-gray-400" 
                style={{ backgroundColor: lineItems.length > 0 ? '#082759' : undefined }} 
                onMouseOver={(e) => lineItems.length > 0 && (e.currentTarget.style.backgroundColor = '#0a2e6b')} 
                onMouseOut={(e) => lineItems.length > 0 && (e.currentTarget.style.backgroundColor = '#082759')}
              >
                Finish Purchase
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          onClose={() => setShowAddProductModal(false)}
          onProductCreated={handleProductCreated}
          categories={categories}
        />
      )}
    </div>
  );
};

export default RecordPurchaseModal;